import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  CircularProgress,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNotificationsManager } from '../../hooks/api/notificationsApiHooks';
import NotificationDropdown from './NotificationDropdown';

interface NotificationIconProps {
  /**
   * Cor do ícone quando não há notificações
   * @default 'inherit'
   */
  color?: 'inherit' | 'primary' | 'secondary' | 'default';
  
  /**
   * Tamanho do ícone
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
}

/**
 * Componente do ícone de notificações que aparece na barra superior
 * Seguindo padrões de design do Material-UI e arquitetura do projeto
 */
const NotificationIcon: React.FC<NotificationIconProps> = ({
  color = 'inherit',
  size = 'medium'
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  
  // Obter token do usuário autenticado
  const token = localStorage.getItem('token') || undefined;
  
  const {
    unreadCount,
    notifications,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    refetch,
    isWebSocketConnected,
    reconnectAttempts
  } = useNotificationsManager(token);

  const isOpen = Boolean(anchorEl);
  const safeUnreadCount = typeof unreadCount === 'number' ? unreadCount : 0;
  const hasUnreadNotifications = safeUnreadCount > 0;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAsRead = (notificationId: number) => {
    markAsRead(notificationId);
  };

  const handleMarkAsUnread = (notificationId: number) => {
    markAsUnread(notificationId);
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const tooltipTitle = isLoading 
    ? 'Carregando notificações...'
    : !isWebSocketConnected && reconnectAttempts > 0
      ? `Reconectando... (${reconnectAttempts}/10)`
    : hasUnreadNotifications 
      ? `${safeUnreadCount} notificação${safeUnreadCount > 1 ? 'ões' : ''} não lida${safeUnreadCount > 1 ? 's' : ''}`
      : 'Nenhuma notificação nova';

  return (
    <>
      <Tooltip title={tooltipTitle} arrow>
        <IconButton
          color={color}
          size={size}
          onClick={handleClick}
          aria-label="notificações"
          aria-describedby={isOpen ? 'notifications-popover' : undefined}
          sx={{
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.12)',
            }
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="secondary" />
          ) : (
            <Badge 
              badgeContent={safeUnreadCount} 
              color="error"
              max={99}
              showZero={false}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.75rem',
                  height: '18px',
                  minWidth: '18px'
                }
              }}
            >
              {hasUnreadNotifications ? (
                <NotificationsIcon sx={{ color: '#fff' }} />
              ) : (
                <NotificationsNoneIcon sx={{ color: '#fff' }} />
              )}
            </Badge>
          )}
        </IconButton>
      </Tooltip>

      <Popover
        id="notifications-popover"
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
          '& .MuiPopover-paper': {
            width: 560,
            maxWidth: '90vw',
            maxHeight: 500,
            boxShadow: theme.shadows[8],
            borderRadius: theme.shape.borderRadius * 1,
          }
        }}
      >
        <Box>
          {error ? (
            <Box p={2}>
              <Typography color="error" align="center">
                Erro ao carregar notificações
              </Typography>
            </Box>
          ) : (
            <NotificationDropdown
              notifications={notifications}
              unreadCount={safeUnreadCount}
              isLoading={isLoading}
              onMarkAsRead={handleMarkAsRead}
              onMarkAsUnread={handleMarkAsUnread}
              onMarkAllAsRead={handleMarkAllAsRead}
              onRefresh={refetch}
              onClose={handleClose}
            />
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationIcon;
